import React, { memo } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { BACKLOG } from '../../../model/constants';
import type { CardListType, CardId } from '../../../model/types';
import { useBacklogSelector } from '../../../state';
import { getIsCompanyManaged } from '../../../state/selectors/app';
import { makeGetCardGroupByParentId } from '../../../state/selectors/card-entries';
import {
	getCardLists,
	makeGetVisibleCardOrderFromCardEntries,
	hasICCOpen,
	hasCardTypesSelector,
	makeIsCardVisible,
	shouldRenderRichElements,
} from '../../../state/selectors/cards';
import { isInlineIssueCreateAvailableForCurrentUser } from '../../../state/selectors/config';
import { makeIsCardListActiveSprint } from '../../../state/selectors/sprints';
import { CardWithIccWrapper } from './view';

export const canShowStatusLozenge = (cardListType: CardListType) => cardListType !== BACKLOG;

export type Props = {
	cardListId: string;
	cardId: CardId;
	isAttachedToParent?: boolean;
	forciblyRenderRichCard?: boolean;
	setRichActiveCard: (cardId: number | null) => void;
	onSelectIssues?: () => void;
};

function ConnectedCardWithIccWrapperInner(props: Props) {
	const { cardId, cardListId, isAttachedToParent, forciblyRenderRichCard, setRichActiveCard } =
		props;
	// Redux hook
	const cardList = useBacklogSelector((state) => getCardLists(state)[cardListId]);
	// Check if the card is not in the backlog (we don't show status lozenges in the backlog)
	const canShowStatusLozengeOnCards = canShowStatusLozenge(cardList.type);

	const visibleCardIds = useBacklogSelector((state) =>
		makeGetVisibleCardOrderFromCardEntries(state)(cardListId),
	);
	// cards that are being hidden by the filter will have index -1.
	// Dnd does not officially support this but it works fine.
	const draggableIndex = visibleCardIds.indexOf(cardId);
	const isActiveSprint = useBacklogSelector((state) =>
		makeIsCardListActiveSprint(state)(cardListId),
	);
	const isIccEnabled = useBacklogSelector(
		(state) =>
			!getIsCompanyManaged(state) &&
			isInlineIssueCreateAvailableForCurrentUser(state) &&
			hasCardTypesSelector(state),
	);
	const isVisible = useBacklogSelector((state) => makeIsCardVisible(state)(cardId));
	const shouldRenderRichCard =
		useBacklogSelector((state) => shouldRenderRichElements(state, { cardId })) ||
		forciblyRenderRichCard === true;
	const hasIccOpen = useBacklogSelector((state) => hasICCOpen(state, { cardId }));
	// This will be defined if this card is a parent of a card group
	const cardGroup = useBacklogSelector(makeGetCardGroupByParentId)(cardListId, cardId);
	const subtaskIds = cardGroup && cardGroup.cardIds;

	return (
		<CardWithIccWrapper
			{...props}
			isVisible={isVisible}
			shouldRenderRichCard={shouldRenderRichCard}
			hasIccOpen={hasIccOpen}
			setRichActiveCard={setRichActiveCard}
			isIccEnabled={isIccEnabled}
			draggableIndex={draggableIndex}
			isActiveSprint={isActiveSprint}
			canShowStatusLozengeOnCards={canShowStatusLozengeOnCards}
			isAttachedToParent={isAttachedToParent}
			subtaskIds={subtaskIds}
			{...(fg('bulk_operations_toolbar_milestone_1')
				? {
						onSelectIssues: props.onSelectIssues,
					}
				: {})}
		/>
	);
}

const ConnectedCardWithIccWrapper = memo(ConnectedCardWithIccWrapperInner);

export default ConnectedCardWithIccWrapper;
